import { useEffect, useMemo } from 'react';
import { buildMyAppsEntries } from './myAppsSearch';
import type { MyAppsNavEntry } from './myAppsNav';
import { PORTAL_MY_APPS_REGISTRY } from './portalMyAppsRegistry';
import type { MyAppsRegistryEntry, MyAppsSearchResult } from './myAppsTypes';
import { useMyAppsStore } from './useMyAppsStore';

export interface MyAppsEntriesResult {
  status: 'idle' | 'loading' | 'ready' | 'error';
  error: string | null;
  entries: readonly MyAppsSearchResult[];
  entriesById: ReadonlyMap<string, MyAppsSearchResult>;
  navEntriesById: ReadonlyMap<string, MyAppsNavEntry>;
  /** Custom app ids, which always show in the sidebar whether pinned or not. */
  customAppIds: readonly string[];
  pinnedToolIds: string[];
  recentToolIds: string[];
  reload: () => Promise<void>;
}

/**
 * Loads My Apps state once and derives the launchable entries a site needs for its
 * nav, launcher, and dashboard card.
 */
export function useMyAppsEntries(
  registry: readonly MyAppsRegistryEntry[] = PORTAL_MY_APPS_REGISTRY
): MyAppsEntriesResult {
  const status = useMyAppsStore((s) => s.status);
  const error = useMyAppsStore((s) => s.error);
  const catalog = useMyAppsStore((s) => s.catalog);
  const externalApps = useMyAppsStore((s) => s.externalApps);
  const pinnedToolIds = useMyAppsStore((s) => s.pinnedToolIds);
  const recentToolIds = useMyAppsStore((s) => s.recentToolIds);
  const load = useMyAppsStore((s) => s.load);

  useEffect(() => {
    if (status === 'idle') void load();
  }, [load, status]);

  const entries = useMemo(
    () => buildMyAppsEntries(catalog, externalApps, registry),
    [catalog, externalApps, registry]
  );
  const entriesById = useMemo(
    () => new Map(entries.map((entry) => [entry.id, entry])),
    [entries]
  );
  const navEntriesById = useMemo(
    () =>
      new Map<string, MyAppsNavEntry>(
        entries.map((entry) => [
          entry.id,
          entry.url
            ? { id: entry.id, title: entry.title, path: entry.path, url: entry.url }
            : { id: entry.id, title: entry.title, path: entry.path }
        ])
      ),
    [entries]
  );
  const customAppIds = useMemo(
    () => entries.filter((entry) => entry.kind === 'link').map((entry) => entry.id),
    [entries]
  );

  return {
    status,
    error,
    entries,
    entriesById,
    navEntriesById,
    customAppIds,
    pinnedToolIds,
    recentToolIds,
    reload: load
  };
}
